import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Calculator } from "lucide-react";
import { toast } from "sonner";
import PromilCalculating from "@/components/PromilCalculating";

const DRINK_TYPES = [
  "Bira 33cl",
  "Bira 50cl",
  "Şarap 150ml",
  "Rakı Tek 4cl",
  "Rakı Duble 8cl",
  "Viski/Votka/Cin Duble 50ml",
  "Kokteyl 200ml",
];

const CalculatorForm = ({ onCalculate }) => {
  const [gender, setGender] = useState("male");
  const [weight, setWeight] = useState("");
  const [hoursPassed, setHoursPassed] = useState("");
  const [drinks, setDrinks] = useState([
    { drink_type: "Bira 50cl", quantity: 1 },
  ]);
  const [loading, setLoading] = useState(false);
  const [calculatedValue, setCalculatedValue] = useState(null);
  const [showCalculating, setShowCalculating] = useState(false);

  const addDrink = () => {
    setDrinks([...drinks, { drink_type: "Bira 50cl", quantity: 1 }]);
  };

  const removeDrink = (index) => {
    if (drinks.length > 1) {
      setDrinks(drinks.filter((_, i) => i !== index));
    }
  };

  const updateDrink = (index, field, value) => {
    const newDrinks = [...drinks];
    newDrinks[index][field] = value;
    setDrinks(newDrinks);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!weight || parseFloat(weight) <= 0) {
      toast.error("Lütfen geçerli bir kilo girin");
      return;
    }

    if (!hoursPassed || parseFloat(hoursPassed) < 0) {
      toast.error("Lütfen geçen süreyi girin");
      return;
    }

    setLoading(true);

    const calculationData = {
      gender,
      weight: parseFloat(weight),
      hours_passed: parseFloat(hoursPassed),
      drinks: drinks.map((d) => ({
        drink_type: d.drink_type,
        quantity: parseInt(d.quantity),
      })),
    };

    let totalPureAlcohol = 0;
    const drinkData = {
      "Bira 33cl": { volume: 330, alcohol: 5.0 },
      "Bira 50cl": { volume: 500, alcohol: 5.0 },
      "Şarap 150ml": { volume: 150, alcohol: 12.0 },
      "Rakı Tek 4cl": { volume: 40, alcohol: 45.0 },
      "Rakı Duble 8cl": { volume: 80, alcohol: 45.0 },
      "Viski/Votka/Cin Duble 50ml": { volume: 50, alcohol: 40.0 },
      "Kokteyl 200ml": { volume: 200, alcohol: 15.0 },
    };

    drinks.forEach((drink) => {
      const info = drinkData[drink.drink_type];
      if (info) {
        const pureAlcoholPerDrink = info.volume * (info.alcohol / 100) * 0.789;
        totalPureAlcohol += pureAlcoholPerDrink * parseInt(drink.quantity);
      }
    });

    const bodyWaterFactor = gender === "male" ? 0.68 : 0.55;
    const totalBodyWater = parseFloat(weight) * bodyWaterFactor;
    const rawBac = totalPureAlcohol / totalBodyWater;
    const currentBac = Math.max(0, rawBac - 0.15 * parseFloat(hoursPassed));

    let legalStatus = "safe";
    let warningMessage = "Yasal sınırın altındasınız, ancak metabolizma farkları sebebiyle gerçek değer değişebilir. En kesin sonuç için Alcodot ile test edin.";
    let penaltyInfo = null;

    if (currentBac >= 1.00) {
      legalStatus = "critical";
      warningMessage = "KRİTİK SEVİYE - ADLİ SÜREÇ RİSKİ";
      penaltyInfo = "⚠️ ÇOK CİDDİ DURUM (1.00‰ ve üzeri)\n\nBu seviye Türk Ceza Kanunu 179. madde kapsamında 'Trafik Güvenliğini Tehlikeye Sokma' suçuna konu olabilir.\n\n• İdari ceza: 9.268 TL + 6 ay ehliyet iptali\n• ADLİ SÜREÇ: Savcılık soruşturması, ifade alma, yargılama riski\n• Kaza durumunda: Ağırlaştırılmış ceza + hapis cezası\n• Ölümlü/yaralanmalı kaza: TCK 179 kapsamında ciddi hapis cezası\n• Cihaza üfleme reddi: 26.558 TL + 2 yıl ehliyet iptali\n\n⚖️ Bu promil seviyesi cezai sorumluluğu artıran unsur sayılır.";
    } else if (currentBac >= 0.50) {
      legalStatus = "illegal";
      warningMessage = "YASAL SINIRI AŞTINIZ";
      penaltyInfo = "• İlk yakalanmada tahmini ceza: 9.268 TL\n• Ehliyetinize 6 ay süreyle el konulur\n• Cihaza üflemeyi reddederseniz: 26.558 TL ceza + 2 yıl ehliyet iptali uygulanır";
    } else if (currentBac >= 0.40) {
      legalStatus = "border";
      warningMessage = "Sınırdasınız! Metabolizmaya bağlı değişkenlik sebebiyle gerçek değer 0.50'nin üzerine çıkabilir. Risk almadan önce Alcodot ile emin olun.";
      penaltyInfo = "⚠️ Yasal sınır 0.50 promildir. Sınırda olduğunuz için risk almayın!";
    }

    const result = {
      current_bac: parseFloat(currentBac.toFixed(2)),
      raw_bac: parseFloat(rawBac.toFixed(2)),
      pure_alcohol_grams: parseFloat(totalPureAlcohol.toFixed(2)),
      legal_status: legalStatus,
      warning_message: warningMessage,
      penalty_info: penaltyInfo,
    };

    // Show calculating animation
    setCalculatedValue(result.current_bac);
    setShowCalculating(true);
    
    // Store data for later
    setTimeout(() => {
      setLoading(false);
    }, 100);
  };

  const handleCalculatingComplete = () => {
    setShowCalculating(false);
    
    const calculationData = {
      gender,
      weight: parseFloat(weight),
      hours_passed: parseFloat(hoursPassed),
      drinks: drinks.map((d) => ({
        drink_type: d.drink_type,
        quantity: parseInt(d.quantity),
      })),
    };

    let totalPureAlcohol = 0;
    const drinkData = {
      "Bira 33cl": { volume: 330, alcohol: 5.0 },
      "Bira 50cl": { volume: 500, alcohol: 5.0 },
      "Şarap 150ml": { volume: 150, alcohol: 12.0 },
      "Rakı Tek 4cl": { volume: 40, alcohol: 45.0 },
      "Rakı Duble 8cl": { volume: 80, alcohol: 45.0 },
      "Viski/Votka/Cin Duble 50ml": { volume: 50, alcohol: 40.0 },
      "Kokteyl 200ml": { volume: 200, alcohol: 15.0 },
    };

    drinks.forEach((drink) => {
      const info = drinkData[drink.drink_type];
      if (info) {
        const pureAlcoholPerDrink = info.volume * (info.alcohol / 100) * 0.789;
        totalPureAlcohol += pureAlcoholPerDrink * parseInt(drink.quantity);
      }
    });

    const bodyWaterFactor = gender === "male" ? 0.68 : 0.55;
    const totalBodyWater = parseFloat(weight) * bodyWaterFactor;
    const rawBac = totalPureAlcohol / totalBodyWater;
    const currentBac = Math.max(0, rawBac - 0.15 * parseFloat(hoursPassed));

    let legalStatus = "safe";
    let warningMessage = "Yasal sınırın altındasınız, ancak metabolizma farkları sebebiyle gerçek değer değişebilir. En kesin sonuç için Alcodot ile test edin.";
    let penaltyInfo = null;

    if (currentBac >= 1.00) {
      legalStatus = "critical";
      warningMessage = "KRİTİK SEVİYE - ADLİ SÜREÇ RİSKİ";
      penaltyInfo = "⚠️ ÇOK CİDDİ DURUM (1.00‰ ve üzeri)\n\nBu seviye Türk Ceza Kanunu 179. madde kapsamında 'Trafik Güvenliğini Tehlikeye Sokma' suçuna konu olabilir.\n\n• İdari ceza: 9.268 TL + 6 ay ehliyet iptali\n• ADLİ SÜREÇ: Savcılık soruşturması, ifade alma, yargılama riski\n• Kaza durumunda: Ağırlaştırılmış ceza + hapis cezası\n• Ölümlü/yaralanmalı kaza: TCK 179 kapsamında ciddi hapis cezası\n• Cihaza üfleme reddi: 26.558 TL + 2 yıl ehliyet iptali\n\n⚖️ Bu promil seviyesi cezai sorumluluğu artıran unsur sayılır.";
    } else if (currentBac >= 0.50) {
      legalStatus = "illegal";
      warningMessage = "YASAL SINIRI AŞTINIZ";
      penaltyInfo = "• İlk yakalanmada tahmini ceza: 9.268 TL\n• Ehliyetinize 6 ay süreyle el konulur\n• Cihaza üflemeyi reddederseniz: 26.558 TL ceza + 2 yıl ehliyet iptali uygulanır";
    } else if (currentBac >= 0.40) {
      legalStatus = "border";
      warningMessage = "Sınırdasınız! Metabolizmaya bağlı değişkenlik sebebiyle gerçek değer 0.50'nin üzerine çıkabilir. Risk almadan önce Alcodot ile emin olun.";
      penaltyInfo = "⚠️ Yasal sınır 0.50 promildir. Sınırda olduğunuz için risk almayın!";
    }

    const result = {
      current_bac: parseFloat(currentBac.toFixed(2)),
      raw_bac: parseFloat(rawBac.toFixed(2)),
      pure_alcohol_grams: parseFloat(totalPureAlcohol.toFixed(2)),
      legal_status: legalStatus,
      warning_message: warningMessage,
      penalty_info: penaltyInfo,
    };

    onCalculate(calculationData, result);
  };

  return (
    <>
      {showCalculating && (
        <PromilCalculating
          finalValue={calculatedValue}
          onComplete={handleCalculatingComplete}
        />
      )}
      
      <div className="glass-card-premium rounded-3xl p-8 sm:p-10 max-w-2xl mx-auto neon-border hover-lift">
        <h3 className="text-3xl font-black mb-8 text-gradient" data-testid="calculator-form-title">
          Bilgilerinizi Girin
        </h3>

      <form onSubmit={handleSubmit} className="space-y-7">
        {/* Gender Selection */}
        <div className="space-y-3">
          <Label className="text-sm font-bold text-teal-400 uppercase tracking-wider">Cinsiyet</Label>
          <div className="grid grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => setGender("male")}
              className={`py-4 px-6 rounded-2xl font-bold smooth-transition text-lg ${
                gender === "male"
                  ? "bg-gradient-to-r from-teal-500 to-cyan-600 text-black shadow-lg neon-glow"
                  : "bg-gray-900/50 border-2 border-teal-500/30 text-gray-300 hover:border-teal-500/60 hover:bg-gray-800/50"
              }`}
              data-testid="gender-male-btn"
            >
              Erkek
            </button>
            <button
              type="button"
              onClick={() => setGender("female")}
              className={`py-4 px-6 rounded-2xl font-bold smooth-transition text-lg ${
                gender === "female"
                  ? "bg-gradient-to-r from-teal-500 to-cyan-600 text-black shadow-lg neon-glow"
                  : "bg-gray-900/50 border-2 border-teal-500/30 text-gray-300 hover:border-teal-500/60 hover:bg-gray-800/50"
              }`}
              data-testid="gender-female-btn"
            >
              Kadın
            </button>
          </div>
        </div>

        {/* Weight */}
        <div className="space-y-3">
          <Label htmlFor="weight" className="text-sm font-bold text-teal-400 uppercase tracking-wider">
            Kilo (kg)
          </Label>
          
          {/* Quick select buttons */}
          <div className="flex gap-2 mb-3">
            {[50, 60, 70, 80, 90, 100].map((w) => (
              <button
                key={w}
                type="button"
                onClick={() => setWeight(w.toString())}
                className={`flex-1 py-2 px-3 rounded-xl text-sm font-bold smooth-transition ${
                  weight === w.toString()
                    ? "bg-teal-500 text-black"
                    : "bg-gray-900/30 border border-teal-500/30 text-gray-400 hover:border-teal-500/60"
                }`}
              >
                {w}
              </button>
            ))}
          </div>
          
          {/* Slider */}
          <Slider
            value={[parseFloat(weight) || 70]}
            onValueChange={(value) => setWeight(value[0].toString())}
            min={40}
            max={150}
            step={1}
            className="mb-3"
          />
          
          {/* Manual Input */}
          <Input
            id="weight"
            type="number"
            step="0.1"
            placeholder="Örn: 75"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            className="h-14 rounded-2xl bg-gray-900/50 border-2 border-teal-500/30 focus:border-teal-500 text-white text-lg font-semibold placeholder:text-gray-500 text-center"
            data-testid="weight-input"
          />
        </div>

        {/* Hours Passed */}
        <div className="space-y-3">
          <Label htmlFor="hours" className="text-sm font-bold text-teal-400 uppercase tracking-wider">
            İçmeye başladığından beri geçen süre (saat)
          </Label>
          
          {/* Quick select buttons */}
          <div className="grid grid-cols-6 gap-2 mb-3">
            {[0.5, 1, 1.5, 2, 3, 4].map((h) => (
              <button
                key={h}
                type="button"
                onClick={() => setHoursPassed(h.toString())}
                className={`py-2 px-2 rounded-xl text-sm font-bold smooth-transition ${
                  parseFloat(hoursPassed) === h
                    ? "bg-teal-500 text-black"
                    : "bg-gray-900/30 border border-teal-500/30 text-gray-400 hover:border-teal-500/60"
                }`}
              >
                {h}s
              </button>
            ))}
          </div>
          
          {/* Slider */}
          <Slider
            value={[parseFloat(hoursPassed) || 2]}
            onValueChange={(value) => setHoursPassed(value[0].toString())}
            min={0}
            max={8}
            step={0.5}
            className="mb-3"
          />
          
          {/* Manual Input */}
          <Input
            id="hours"
            type="number"
            step="0.5"
            placeholder="Örn: 2"
            value={hoursPassed}
            onChange={(e) => setHoursPassed(e.target.value)}
            className="h-14 rounded-2xl bg-gray-900/50 border-2 border-teal-500/30 focus:border-teal-500 text-white text-lg font-semibold placeholder:text-gray-500 text-center"
            data-testid="hours-input"
          />
        </div>

        {/* Drinks List */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-bold text-teal-400 uppercase tracking-wider">İçki Listesi</Label>
            <Button
              type="button"
              onClick={addDrink}
              variant="outline"
              size="sm"
              className="flex items-center gap-2 rounded-xl bg-teal-500/10 border-teal-500/50 text-teal-400 hover:bg-teal-500/20 hover:border-teal-500 font-bold"
              data-testid="add-drink-btn"
            >
              <Plus className="w-4 h-4" />
              Ekle
            </Button>
          </div>

          <div className="space-y-4">
            {drinks.map((drink, index) => (
              <div
                key={index}
                className="flex gap-4 items-end bg-gray-900/50 p-5 rounded-2xl border-2 border-teal-500/20 hover:border-teal-500/40 smooth-transition"
              >
                <div className="flex-1 space-y-2">
                  <Label className="text-xs text-teal-300 font-semibold uppercase">İçki Türü</Label>
                  <Select
                    value={drink.drink_type}
                    onValueChange={(value) => updateDrink(index, "drink_type", value)}
                  >
                    <SelectTrigger className="h-12 rounded-xl bg-black/50 border-teal-500/30 text-white" data-testid={`drink-type-select-${index}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-900 border-teal-500/30">
                      {DRINK_TYPES.map((type) => (
                        <SelectItem key={type} value={type} className="text-white hover:bg-teal-500/20">
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="w-28 space-y-2">
                  <Label className="text-xs text-teal-300 font-semibold uppercase">Adet</Label>
                  <Input
                    type="number"
                    min="1"
                    value={drink.quantity}
                    onChange={(e) =>
                      updateDrink(index, "quantity", e.target.value)
                    }
                    className="h-12 rounded-xl bg-black/50 border-teal-500/30 text-white text-center font-bold"
                    data-testid={`drink-quantity-input-${index}`}
                  />
                </div>

                {drinks.length > 1 && (
                  <Button
                    type="button"
                    onClick={() => removeDrink(index)}
                    variant="ghost"
                    size="sm"
                    className="h-12 w-12 p-0 text-red-400 hover:bg-red-500/10 hover:text-red-300 rounded-xl border-2 border-transparent hover:border-red-500/30"
                    data-testid={`remove-drink-btn-${index}`}
                  >
                    <Trash2 className="w-5 h-5" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={loading}
          className="w-full h-16 rounded-2xl bg-gradient-to-r from-teal-500 via-cyan-500 to-sky-500 hover:from-teal-400 hover:via-cyan-400 hover:to-sky-400 text-black font-black text-xl shadow-lg neon-glow smooth-transition shimmer"
          data-testid="calculate-btn"
        >
          <Calculator className="w-6 h-6 mr-3" />
          Promil Hesapla
        </Button>
      </form>
    </div>
    </>
  );
};

export default CalculatorForm;